import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Clock, CheckCircle2, Phone, Mail, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const BookingSection = () => {
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [time, setTime] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);

  useEffect(() => {
    if (!date) return;
    setTime(null);
    api
      .get(`/slots?date=${fmtDate(date)}`)
      .then(({ data }) => setSlots(data.slots))
      .catch(() => setSlots([]));
  }, [date]);

  const submit = async () => {
    if (!name || !email || !time) {
      toast.error("Merci de remplir votre nom, email et de choisir un créneau.");
      return;
    }
    if (!consent) {
      toast.error("Merci d'accepter la politique de confidentialité.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/appointments", {
        name,
        email,
        date: fmtDate(date),
        time,
        note,
        language: navigator.language?.split("-")[0] || "fr",
      });
      setDone(true);
      toast.success("Rendez-vous confirmé et ajouté à l'agenda !");
      api.get(`/slots?date=${fmtDate(date)}`).then(({ data }) => setSlots(data.slots));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réservation.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="rendez-vous" className="relative border-t border-border bg-secondary/40 px-6 py-24 md:py-32">
      <div className="mx-auto max-w-6xl">
        <div className="mb-14 max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Rendez-vous</span>
          <h2 className="mt-4 text-4xl font-bold tracking-tighter sm:text-5xl">
            Réservez votre étude gratuite
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Choisissez un créneau. Il sera ajouté <strong>directement à l'agenda</strong> de votre installateur, et
            l'assistante IA vous répondra dans votre langue.
          </p>
        </div>

        {done ? (
          <motion.div
            data-testid="booking-success"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mx-auto flex max-w-xl flex-col items-center gap-5 rounded-3xl border border-border bg-card p-10 text-center"
          >
            <CheckCircle2 className="h-16 w-16 text-primary" />
            <h3 className="text-2xl font-bold tracking-tight">Rendez-vous bien noté !</h3>
            <p className="text-muted-foreground">
              Merci {name}. Votre rendez-vous est enregistré. Vous serez contacté(e) par{" "}
              <strong className="text-foreground">Monsieur Olivier Toussaint</strong>.
            </p>
            <div className="w-full rounded-2xl bg-secondary/50 p-5 text-left" data-testid="booking-confirmation-details">
              <p className="flex items-center gap-2 text-sm font-semibold text-primary">
                <CalendarDays className="h-4 w-4" />
                {date.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                {" "}à {time}
              </p>
              <div className="mt-4 space-y-2 border-t border-border/60 pt-4 text-sm text-muted-foreground">
                <p className="flex items-center gap-2"><User className="h-4 w-4 text-accent" /> Contact : Olivier Toussaint</p>
                <a href="tel:+32472195245" className="flex items-center gap-2 transition-colors hover:text-accent" data-testid="confirmation-phone">
                  <Phone className="h-4 w-4 text-accent" /> +32 472 19 52 45
                </a>
                <a href="mailto:euroconsult.eu@gmail.com" className="flex items-center gap-2 transition-colors hover:text-accent" data-testid="confirmation-email">
                  <Mail className="h-4 w-4 text-accent" /> euroconsult.eu@gmail.com
                </a>
              </div>
            </div>
            <Button
              data-testid="booking-reset-button"
              variant="outline"
              onClick={() => {
                setDone(false);
                setTime(null);
                setName("");
                setEmail("");
                setNote("");
                setConsent(false);
              }}
            >
              Prendre un autre rendez-vous
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="rounded-3xl border border-border bg-card p-6 lg:col-span-5">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold">
                <CalendarDays className="h-4 w-4 text-accent" /> Choisissez une date
              </div>
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => d && setDate(d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                className="rounded-xl"
                data-testid="booking-calendar"
              />
            </div>

            <div className="rounded-3xl border border-border bg-card p-6 lg:col-span-7">
              <div className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Clock className="h-4 w-4 text-accent" /> Créneaux disponibles
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {slots.map((s) => (
                  <button
                    key={s.time}
                    data-testid={`slot-${s.time}`}
                    disabled={!s.available}
                    onClick={() => setTime(s.time)}
                    className={`rounded-full border px-3 py-2 text-sm font-medium transition-colors ${
                      time === s.time
                        ? "border-accent bg-accent text-accent-foreground"
                        : s.available
                        ? "border-border bg-background hover:border-accent"
                        : "cursor-not-allowed border-border bg-muted text-muted-foreground/40 line-through"
                    }`}
                  >
                    {s.time}
                  </button>
                ))}
              </div>

              <div className="mt-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="mb-1.5 block text-sm">Nom complet</Label>
                    <Input
                      data-testid="booking-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Jean Dupont"
                      className="h-12"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block text-sm">Email</Label>
                    <Input
                      data-testid="booking-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jean@email.com"
                      className="h-12"
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block text-sm">Message (optionnel)</Label>
                  <Textarea
                    data-testid="booking-note-input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Type de logement, questions..."
                    rows={3}
                  />
                </div>
                <div className="flex items-start gap-3" data-testid="booking-consent">
                  <Checkbox
                    id="booking-consent-checkbox"
                    data-testid="booking-consent-checkbox"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(!!v)}
                    className="mt-0.5"
                  />
                  <label htmlFor="booking-consent-checkbox" className="text-xs leading-relaxed text-muted-foreground">
                    J'accepte que mes données (nom, e-mail, message) soient utilisées uniquement pour traiter ma demande
                    de rendez-vous, conformément à la{" "}
                    <a href="/confidentialite" className="text-accent underline">politique de confidentialité</a>.
                  </label>
                </div>
                <Button
                  data-testid="booking-submit-button"
                  onClick={submit}
                  disabled={submitting || !consent}
                  className="h-12 w-full rounded-full bg-accent text-base hover:bg-accent/90"
                >
                  {submitting ? "Confirmation..." : "Confirmer le rendez-vous"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
